import type {
    IConfigAPI,
    IFetcher,
    ILog,
    IProgressPromise,
    IProgressPromiseGenerator,
} from '@monofe/util';
import type { IMediaURLResolverParameters, MediaBinaryStorageTypeMap } from '../interface/media';
import mediaDownload from './media.download';
import mediaUtil from './media.util';

const FETCH_RETRY_COUNT = 3;

const _hasHttpPrefix = (url = '') =>
    url.startsWith('http://') || url.startsWith('https://') || false;

function _requestFromConfig(config: IConfigAPI, mediaId: string): string | undefined {
    if (!config.extension.media) {
        return undefined;
    }

    const templatedPath = _hasHttpPrefix(config.extension.media)
        ? config.extension.media
        : `${config.url}${config.extension.media}`;

    const resolvedPath = templatedPath ? templatedPath.replace('%MEDIAID%', mediaId) : undefined;

    return _hasHttpPrefix(resolvedPath) ? resolvedPath : undefined;
}

function _requestFromConfigWithToken(
    config: IConfigAPI,
    mediaId: string,
    accessToken: string,
): string | undefined {
    if (!config.extension.mediaWithAccessToken) {
        return undefined;
    }

    const templatedPath = _hasHttpPrefix(config.extension.mediaWithAccessToken)
        ? config.extension.mediaWithAccessToken
        : `${config.url || ''}${config.extension.mediaWithAccessToken}`;

    const resolvedPath = templatedPath
        ? templatedPath.replace('%MEDIAID%', mediaId).replace('%ACCESS_TOKEN%', accessToken)
        : undefined;

    return _hasHttpPrefix(resolvedPath) ? resolvedPath : undefined;
}

function canResolveMedium(mediaId: string): Promise<boolean> {
    /* No way to query for this, just assume we can */
    return Promise.resolve(true);
}

function requestURL(
    config: IConfigAPI,
    mediaId: string,
    parameters: IMediaURLResolverParameters = {},
): string[] {
    let resolver = (mediaId: string, config: IConfigAPI) => _requestFromConfig(config, mediaId);

    const authToken = parameters.accessToken;

    if (authToken !== undefined) {
        resolver = (mediaId: string, config: IConfigAPI) =>
            _requestFromConfigWithToken(config, mediaId, authToken);
    }

    const result = resolver(mediaId, config);

    return result !== undefined ? [result] : [];
}

function getMedia<
    K extends keyof MediaBinaryStorageTypeMap,
    V extends MediaBinaryStorageTypeMap[K],
>(
    promiser: IProgressPromiseGenerator,
    config: IConfigAPI,
    fetcher: IFetcher,
    logger: ILog,
    type: K,
    mediaId: string,
    parameters: IMediaURLResolverParameters,
): IProgressPromise<V> {
    const urls = requestURL(config, mediaId, parameters);
    const requests = urls.map(url => mediaUtil.requestForURL(url, type));

    return mediaDownload.download(promiser, fetcher, logger, requests, type, FETCH_RETRY_COUNT);
}

export default Object.freeze({
    canResolveMedium,
    getMedia,
    requestURL,
});
